/* eslint-disable max-lines */

import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { NavLink } from 'react-router-dom'

import Checkbox from 'components/Checkbox'
import FavoriteIcon from 'pages/DeviceSearch/FavoriteIcon'
import Tooltip from 'components/Tooltip'

import Api from 'services/api'

import { useSessionContext } from 'hooks/SessionContext'

import { color } from 'styles/colors'
import { screenSize } from 'styles/shared'

const propTypes = {
  pathRoot: PropTypes.string.isRequired,
  deviceId: PropTypes.string.isRequired,
  updateSelectedDevicesIdsCallback: PropTypes.func.isRequired,
  selectedDeviceIds: PropTypes.arrayOf(PropTypes.shape).isRequired,
  favorites: PropTypes.arrayOf(PropTypes.string),
  note: PropTypes.objectOf(PropTypes.shape),
  serialNumber: PropTypes.string,
  dealerPhoneNumber: PropTypes.string,
  dealerName: PropTypes.string,
  getDeviceLinkCallback: PropTypes.func
}

const defaultProps = {
  favorites: [],
  note: null,
  serialNumber: '',
  dealerPhoneNumber: '',
  dealerName: ''
}

const ListItem = ({ pathRoot, note, deviceId, selectedDeviceIds, dealerName, dealerPhoneNumber, serialNumber, updateSelectedDevicesIdsCallback, getDeviceLinkCallback }) => {
  const { session: { isAdmin, isFsr, isTechnician, dealerId } } = useSessionContext()
  const storageId = pathRoot + 'Selected'
  const [deviceSelected, setDeviceSelected] = useState(false)

  const [value, setValue] = useState(note ? note.content : '')
  const [oldContent, setOldContent] = useState(note ? note.content : '')
  const [error, setError] = useState(false)
  const [saved, setSaved] = useState(false)

  const isUserValidForFavorites = isFsr || isAdmin || isTechnician
  let timeOut

  useEffect(() => {
    setDeviceSelected(selectedDeviceIds.includes(deviceId))
  }, [selectedDeviceIds, deviceId])

  const onSave = () => {
    if ((value && value !== oldContent)) {
      setSaved(false)

      if (timeOut) {
        clearTimeout(timeOut)
      }

      Api.Notes.create(dealerId, deviceId, value)
        .then(() => {
          setOldContent(value)
          setError(false)
          setSaved(true)
        })
        .catch(() => {
          setError(true)
          setSaved(false)
        }).finally(() => {
          timeOut = setTimeout(() => {
            setSaved(false)
            setError(false)
          }, 3000)
        })
    }
  }

  const onChange = () => {
    if (deviceSelected) {
      updateSelectedDevicesIdsCallback(selectedDeviceIds.filter(v => v !== deviceId))
    } else {
      updateSelectedDevicesIdsCallback([...selectedDeviceIds, deviceId])
    }

    setDeviceSelected(!deviceSelected)
  }

  return (
    <S.Container {...{ isTechnician }}>
      <S.LeftSide {...{ isTechnician }}>
        {isUserValidForFavorites && <S.FavoriteIcon {...{ deviceId }} />}

        <S.Checkbox label="" checked={deviceSelected} {...{ onChange, isUserValidForFavorites }} />

        <S.Auid>
          AUID:

          <S.DeviceIdLink
            to={{
              pathname: `/${pathRoot}/multiple/${getDeviceLinkCallback(deviceId)}`,
              state: { selectedDevicesStorageId: storageId }
            }}
            onClick={() => { localStorage.setItem(storageId, deviceId) }}
          >
            {deviceId}
          </S.DeviceIdLink>
        </S.Auid>

        <S.SerialNumber>S/N: {serialNumber || '--'}</S.SerialNumber>

        <S.DealerInfo>Dealer: {dealerName ? `${dealerName}, ${dealerPhoneNumber}` : '--'}</S.DealerInfo>
      </S.LeftSide>

      {!isTechnician && (
        <S.RightSide>
          <S.NoteInput
            type="text"
            value={value}
            placeholder="Notes"
            onChange={(e) => setValue(e.target.value)}
            onBlur={onSave}
            onKeyPress={(e) => { if (e.key === 'Enter') onSave() }}
          />
          <S.AlertContainer>
            {saved && <S.SavedAlert saved={saved}><i className="icon-ok" />Saved</S.SavedAlert>}

            {error && (
              <Tooltip title="The note failed to save" placement="top">
                <S.ErrorAlert><i className="icon-remove" />  Error</S.ErrorAlert>
              </Tooltip>
            )}
          </S.AlertContainer>
        </S.RightSide>
      )}
    </S.Container>
  )
}

ListItem.propTypes = propTypes
ListItem.defaultProps = defaultProps

export default ListItem

const S = {
  Container: styled.div.withConfig({
    shouldForwardProp: (prop) => !['isTechnician'].includes(prop)
  })`
    margin-bottom: ${({ isTechnician }) => isTechnician ? '13px' : '0'};

    @media ${screenSize.small} {
      margin-bottom: ${({ isTechnician }) => isTechnician ? '9px' : '2px'};
    }
  `,
  LeftSide: styled.div.withConfig({
    shouldForwardProp: (prop) => !['isTechnician'].includes(prop)
  })`
    display: inline-block;
    vertical-align: top;
    width: ${({ isTechnician }) => isTechnician ? '100%' : '50%'};
    overflow: hidden;

    @media ${screenSize.medium} {
      display: block;
      width: 100%;
    }
  `,
  RightSide: styled.div.withConfig({
    shouldForwardProp: (prop) => !['isTechnician'].includes(prop)
  })`
    display: inline-flex;
    width: 50%;
    vertical-align: text-bottom;

    @media ${screenSize.medium} {
      display: ${({ isTechnician }) => isTechnician ? 'none' : 'flex'};
      ${({ isTechnician }) => !isTechnician && 'width: 100%;'}
    }

    @media ${screenSize.small} {
      display: ${({ isTechnician }) => isTechnician ? 'none' : 'grid'};
      ${({ isTechnician }) => !isTechnician && 'width: 84%;'}
    }
  `,

  FavoriteIcon: styled(FavoriteIcon)`
    margin-right: 10px;
  `,
  Checkbox: styled(Checkbox).withConfig({
    shouldForwardProp: (prop) => !['isUserValidForFavorites'].includes(prop)
  })`
    display: inline-block;
    margin-left: ${({ isUserValidForFavorites }) => isUserValidForFavorites ? '0' : '30'}px;
    vertical-align: top;
  `,
  Auid: styled.div`
    display: inline-block;
    width: 145px;
    vertical-align: top;
  `,
  DeviceIdLink: styled(NavLink)`
    margin-left: 5px;
  `,
  SerialNumber: styled.div`
    display: inline-block;
    width: 140px;
    margin-left: 10px;
    vertical-align: top;

    @media ${screenSize.small} {
      margin-left: 51px;
      margin-bottom: 5px;
      display: block;
    }
  `,
  DealerInfo: styled.div`
    display: inline-block;
    vertical-align: top;
    width: calc(100% - 365px);
    margin-left: 15px;
    margin-bottom: 3px;

    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    @media ${screenSize.small} {
      display: block;
      width: 100%;
      margin-bottom: 5px;
      margin-left: 51px;
      margin-top: -5px;
      word-spacing: 4px;
    }
  `,

  NoteInput: styled.input`
    height: 25px;
    width: calc(100% - 63px);
    margin-top: -3px;

    &::placeholder {
      opacity: 0;
    }

    @media ${screenSize.medium} {
      width: calc(100% - 114px);
      margin-top: 3px;
      margin-left: 52px;

      &::placeholder {
        opacity: 0.5;
      }
    }

    @media ${screenSize.small} {
      width: 84%;
      margin-top: 0;
      margin-left: 51px;
    }
  `,
  AlertContainer: styled.div`
    cursor: default;
    margin-left: 10px;

    @media ${screenSize.small} {
      margin: -15px auto 10px calc(100% - 72px);
    }
  `,
  SavedAlert: styled.div.withConfig({
    shouldForwardProp: (prop) => !['saved'].includes(prop)
  })`
    color: ${color('successText')};

    ${({ saved }) => saved ? '' : `
      opacity: 0;
      transition: all 250ms linear 2s;
    `}
  `,
  ErrorAlert: styled.div`
    color: ${color('errorText')};
  `
}
